import React from 'react';
import isEqual from 'lodash.isequal';
import isNil from 'lodash.isnil';
import PropTypes from 'prop-types';
import isEmpty from 'lodash.isempty';
import { isTrcPresumedResult } from '../../../utils/shared/trcUtils';
import ScoreInterpretationMapper from '../../../constants/shared/ScoreInterpretationMapper';
import { getScore, getScoreInterpretationLabel } from '../../../utils/shared/cellUtils';
import { INTERP, INFO_TIPS } from '../../../constants/shared/CommonConstants';
import { LONG_DASH } from '../../../constants/shared/CellConstants';
import '../../../styles/icons.scss';
import './Cells.scss';
import { createDataTourStep, setDataTourInteraction } from '../../../utils/shared/demoModeUtils';
import {
    SAMEER_FN,
    SUB_HEAD_COMP_COL,
    SUB_HEAD_NAM_COL,
} from '../../../constants/reading/DemoModeConstants';
import RadioGroup from '../../radioGroup/RadioGroup';
import { MEASURE } from '../../../constants/shared/MeasuresConstants';

export const TextCell = ({ title, content, classNames, isDataCell }) => (
    <div
        data-tour-step={createDataTourStep(title)}
        className={`${isDataCell ? 'data-cell' : ''} ${classNames}`}
    >
        {content}
    </div>
);

TextCell.defaultProps = {
    classNames: '',
    content: null,
    isDataCell: true,
    title: '',
};
TextCell.propTypes = {
    classNames: PropTypes.string,
    content: PropTypes.oneOfType([PropTypes.object, PropTypes.string, PropTypes.number]),
    isDataCell: PropTypes.bool,
    title: PropTypes.string,
};

export const TwoPartHeaderCell = props => {
    const { classNames, spanClassNames, firstPartTitle, secondPartTitle, showSortingIcon } = props;
    return (
        <div
            data-tour-step={createDataTourStep(`${firstPartTitle}${secondPartTitle}`)}
            className={classNames}
        >
            <span className={spanClassNames}>
                {firstPartTitle}
                <br />
                {secondPartTitle}
            </span>
            {showSortingIcon && <i className="icon-amplify-sort" />}
        </div>
    );
};
TwoPartHeaderCell.defaultProps = {
    classNames: '',
    spanClassNames: '',
    firstPartTitle: '',
    secondPartTitle: '',
    showSortingIcon: false,
};

TwoPartHeaderCell.propTypes = {
    classNames: PropTypes.string,
    spanClassNames: PropTypes.string,
    firstPartTitle: PropTypes.string,
    secondPartTitle: PropTypes.string,
    showSortingIcon: PropTypes.bool,
};

export const StudentInfoCell = ({ firstName, lastName, classNames }) => (
    <TextCell
        classNames={classNames}
        content={
            <>
                <strong>{`${lastName}, `}</strong>
                {firstName}
            </>
        }
        isDataCell={false}
    />
);

StudentInfoCell.defaultProps = {
    firstName: '',
    lastName: '',
    classNames: '',
};

StudentInfoCell.propTypes = {
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    classNames: PropTypes.string,
};

export const SkillHeaderCell = props => (
    <div className={props.containerClassNames}>
        <div className={props.classNames}>{props.label}</div>
    </div>
);

SkillHeaderCell.defaultProps = {
    label: '',
    classNames: '',
    containerClassNames: '',
};

SkillHeaderCell.propTypes = {
    label: PropTypes.string,
    classNames: PropTypes.string,
    containerClassNames: PropTypes.string,
};

const getGradientLine = isFirstRow => isFirstRow && <div className="gradient-line" />;

export const GradeCompositeCell = ({
    name,
    goalSection,
    isFirstRow,
    isResetButtonAvailableInClass,
}) => (
    <div
        className={`subheader-cell second-column ${
            isResetButtonAvailableInClass && 'with-reset-button'
        }`}
        data-tour-step={SUB_HEAD_COMP_COL}
        data-tour-allow-interaction
    >
        <span>{name}</span>
        {goalSection}
        <i className="icon-amplify-sort" />
        {getGradientLine(isFirstRow)}
    </div>
);

GradeCompositeCell.defaultProps = {
    isFirstRow: false,
    goalSection: null,
    isResetButtonAvailableInClass: false,
};

GradeCompositeCell.propTypes = {
    name: PropTypes.string.isRequired,
    goalSection: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.object]),
    isFirstRow: PropTypes.bool,
    isResetButtonAvailableInClass: PropTypes.bool,
};

export const GradeNameCell = props => (
    <section className="subheader-cell first-column" data-tour-step={SUB_HEAD_NAM_COL}>
        <h2>{props.name}</h2>
        <span>
            <strong>Last Name, </strong>
            First Name
        </span>
        {props.showSortingIcon && <i className="icon-amplify-sort" />}
        {getGradientLine(props.isFirstRow)}
    </section>
);

GradeNameCell.defaultProps = {
    showSortingIcon: false,
    isFirstRow: false,
};

GradeNameCell.propTypes = {
    name: PropTypes.string.isRequired,
    showSortingIcon: PropTypes.bool,
    isFirstRow: PropTypes.bool,
};

export const GradeScoreCell = ({
    name,
    goalSection,
    showSortingIcon,
    optionalCellData,
    isFirstRow,
}) => (
    <div
        data-tour-step={createDataTourStep(name)}
        className={`subheader-cell ${optionalCellData.classNames}`}
    >
        <span>{name}</span>
        {goalSection}
        {showSortingIcon && <i className="icon-amplify-sort" />}
        {getGradientLine(isFirstRow)}
    </div>
);

GradeScoreCell.defaultProps = {
    name: null,
    goalSection: null,
    showSortingIcon: false,
    isFirstRow: false,
    optionalCellData: {},
};

GradeScoreCell.propTypes = {
    name: PropTypes.string,
    goalSection: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.object]),
    showSortingIcon: PropTypes.bool,
    isFirstRow: PropTypes.bool,
    optionalCellData: PropTypes.object,
};

export const StudentNameCell = props => {
    const {
        studentInfo,
        isTransitionalKindergarten,
        isDyslexiaRiskDisplayed,
        isUnofficial,
        navigationUrl,
        LinkElement,
    } = props;
    const { lastName, firstName } = studentInfo;
    return (
        <div className="data-cell first-column">
            <LinkElement
                to={navigationUrl}
                data-tour-step={createDataTourStep(`${firstName}-${lastName}`)}
                data-tour-allow-interaction
            >
                <StudentInfoCell firstName={firstName} lastName={lastName} />
            </LinkElement>
            <div data-tour-step={firstName === SAMEER_FN ? INFO_TIPS : ''} className={INFO_TIPS}>
                {isTransitionalKindergarten && <i className="icon-tk" />}
                {isUnofficial && <i className="asterisk-icon" />}
                {isDyslexiaRiskDisplayed && <i className="icon-amplify-info-outline rotate" />}
            </div>
        </div>
    );
};

StudentNameCell.defaultProps = {
    isDyslexiaRiskDisplayed: false,
    isTransitionalKindergarten: false,
    isUnofficial: false,
    LinkElement: {},
};

StudentNameCell.propTypes = {
    studentInfo: PropTypes.object.isRequired,
    navigationUrl: PropTypes.string.isRequired,
    isDyslexiaRiskDisplayed: PropTypes.bool,
    isTransitionalKindergarten: PropTypes.bool,
    isUnofficial: PropTypes.bool,
    LinkElement: PropTypes.object,
};

export const CommonScoreCell = props => {
    const {
        isCellDisabled,
        interp,
        measure,
        score,
        percentile,
        onScoreClick,
        isComplete,
        optionalCellData,
        proficientLevel,
        getCustomScore,
    } = props;
    const scoreInterpretationLabel = getScoreInterpretationLabel(interp, proficientLevel);
    let firstName;
    let lastName;
    if (optionalCellData.studentInfo) {
        firstName = optionalCellData?.studentInfo?.firstName;
        lastName = optionalCellData?.studentInfo?.lastName;
    }

    return isCellDisabled ? (
        <div className={`data-cell empty-cell ${optionalCellData.classNames}`} />
    ) : (
        <div
            data-tour-allow-interaction={setDataTourInteraction(
                `${firstName}${lastName}${measure}`,
            )}
            data-tour-step={createDataTourStep(`${firstName}${lastName}${measure}`)}
            className={` ${interp} data-cell ${
                isTrcPresumedResult(measure, score) && 'without-hover'
            } ${optionalCellData.classNames}`}
            onClick={onScoreClick}
            role="presentation"
        >
            {getCustomScore(props)}
            {isComplete && scoreInterpretationLabel && (
                <div className="score-interp">{scoreInterpretationLabel}</div>
            )}
            {optionalCellData.isPercentileShown && !isNil(percentile) && (
                <div className="score-interp">Pctl: {percentile}</div>
            )}
        </div>
    );
};

CommonScoreCell.defaultProps = {
    interp: null,
    score: '',
    percentile: null,
    isCellDisabled: false,
    onScoreClick: () => {},
    isComplete: false,
    measure: '',
    optionalCellData: {},
    proficientLevel: '',
    getCustomScore: getScore,
};

CommonScoreCell.propTypes = {
    interp: PropTypes.string,
    isCellDisabled: PropTypes.bool,
    onScoreClick: PropTypes.func,
    isComplete: PropTypes.bool,
    score: PropTypes.oneOfType([PropTypes.number, PropTypes.object, PropTypes.string]),
    percentile: PropTypes.number,
    measure: PropTypes.string,
    proficientLevel: PropTypes.string,
    optionalCellData: PropTypes.object,
    getCustomScore: PropTypes.func,
};

export const InputCell = props => {
    const { isCellDisabled, interp, percentile, onScoreClick, optionalCellData } = props;

    if (isCellDisabled) {
        return <div className={`data-cell empty-cell ${optionalCellData.classNames}`} />;
    }

    return (
        <div
            className={`${interp} data-cell ${optionalCellData.classNames}`}
            onClick={onScoreClick}
            role="presentation"
        >
            <div className="input-cell">
                <div className="box">{getScore(props)}</div>
                {isEqual(interp, INTERP.BLANK) ? (
                    <div>{LONG_DASH}</div>
                ) : (
                    <i className="icon-amplify-admin" />
                )}
            </div>
            <div className="score-interp">{ScoreInterpretationMapper[interp]}</div>
            {optionalCellData.isPercentileShown && !isNil(percentile) && (
                <div className="score-interp">Pctl: {percentile}</div>
            )}
        </div>
    );
};

InputCell.defaultProps = {
    interp: null,
    percentile: null,
    onScoreClick: () => {},
    isCellDisabled: false,
    optionalCellData: {},
};
InputCell.propTypes = {
    interp: PropTypes.string,
    percentile: PropTypes.number,
    onScoreClick: PropTypes.func,
    isCellDisabled: PropTypes.bool,
    optionalCellData: PropTypes.object,
};

export const getOffGradeText = grade => `Gr. ${grade}`;

export const OFF_GRADE_ICON = <div className="off-grade-icon" />;

export const getOffGradeCell = grade =>
    !isNil(grade) && (
        <>
            {` (${getOffGradeText(grade)})`}&nbsp;
            {OFF_GRADE_ICON}
        </>
    );

getOffGradeCell.defaultProps = {
    grade: null,
};

getOffGradeCell.propTypes = {
    grade: PropTypes.string,
};

export const ZogRangeCell = ({
    classNames,
    measure,
    studentId,
    range,
    rangeLabel,
    defaultRange,
    isDataCell,
    onSelectHandler,
    isDemoModeOn,
}) => {
    return (
        <div className={`${isDataCell ? 'data-cell' : ''} ${classNames}`}>
            <div className="font-weight-normal">{isEmpty(rangeLabel) ? range : rangeLabel}</div>
            {!isEmpty(range) && measure !== MEASURE.OVERALL && (
                <RadioGroup
                    values={[range]}
                    isDisabled={isDemoModeOn}
                    groupName={studentId + measure}
                    defaultValue={defaultRange}
                    hasLabel={false}
                    radioBtnClassName="radio-btn"
                    onSelect={onSelectHandler}
                />
            )}
            {measure === MEASURE.OVERALL && !isEmpty(defaultRange) && (
                <i className="selected-mark" />
            )}
        </div>
    );
};

ZogRangeCell.defaultProps = {
    measure: '',
    studentId: '',
    range: null,
    rangeLabel: null,
    defaultRange: null,
    isDataCell: false,
    classNames: '',
    onSelectHandler: () => {},
    isDemoModeOn: false,
};

ZogRangeCell.propTypes = {
    measure: PropTypes.string,
    studentId: PropTypes.string,
    range: PropTypes.string,
    rangeLabel: PropTypes.string,
    defaultRange: PropTypes.string,
    isDataCell: PropTypes.bool,
    classNames: PropTypes.string,
    onSelectHandler: PropTypes.func,
    isDemoModeOn: PropTypes.bool,
};
